import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { isOverdue } from './todoDisplayUtils';
import { ClassifyReceiptDialog } from './ClassifyReceiptDialog';
import { useCardLabel } from '@/i18n/useCardLabel';
import { useSendToClientMutation } from '@/hooks/useSendToClientMutation';
import { useFormatters } from '@financialharmony/ui';
import { useTodoTitle } from '@/i18n/useTodoTitle';
import type { TodoItem } from '@/types/todo';

// FIN-578 PR2 — F19: wires the "Send to client" action. AC49 (pending spinner,
// native `disabled` — double-click can't fire twice). No inline alert on failure: the
// mutation already toasts the specific backend message (TodoApiClient.redirectTodo ->
// handleError()), and useSendToClientMutation refetches on failure too, so the card
// itself re-renders from the true server state (e.g. flips to a Client badge if the
// "failure" was someone else already having sent it) instead of sitting stuck on a
// stale badge next to a red box that just repeats what the toast already said (AC50).
export function ConsultantTodoCard({
  todo,
  showCompanyLabel = false,
}: {
  todo: TodoItem;
  showCompanyLabel?: boolean;
}) {
  const t = useTranslations('consultantTodos.card');
  const tc = useTranslations('consultantCards');
  const cardLabel = useCardLabel();
  const { formatDate } = useFormatters();
  const todoTitle = useTodoTitle();
  const overdue = isOverdue(todo.due_date);
  const urgent = todo.required && overdue;
  const sendToClient = useSendToClientMutation(todo.company_id);
  const [answering, setAnswering] = useState(false);
  // FIN-1165 F3: a classify todo whose receipt shows a card is answered with the receipt beside the
  // question; one with no card read keeps today's flow.
  const canAnswerCard =
    todo.type === 'classify_document' &&
    !!todo.detected_card &&
    todo.linked_record_type === 'financial_document' &&
    !!todo.linked_record_id;

  return (
    <Card
      data-testid={`consultant-todo-card-${todo.id}`}
      className={cn('rounded-lg', urgent ? 'border-l-4 border-l-destructive shadow-md' : 'shadow-sm')}
    >
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{todoTitle(todo)}</p>
          {todo.personal_note && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{todo.personal_note}</p>
          )}
          {canAnswerCard && todo.detected_card && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {tc('paidWith', { card: cardLabel({ provider: todo.detected_card.provider, last_four: todo.detected_card.last_four }) })}
              {todo.card_ambiguous ? ` ${tc('todoCard.ambiguous')}` : ''}
            </p>
          )}
        </div>
        <div className="flex flex-none items-center gap-2">
          {todo.due_date && (
            <Badge variant={overdue ? 'destructive' : 'secondary'}>{t('due', { date: formatDate(todo.due_date, 'dayMonth') })}</Badge>
          )}
          {showCompanyLabel && todo.companies?.name && (
            <Badge variant="secondary" data-testid="todo-company-badge">{todo.companies.name}</Badge>
          )}
          <Badge variant={todo.audience === 'internal' ? 'outline' : 'default'}>
            {todo.audience === 'internal' ? t('internal') : t('client')}
          </Badge>
          {canAnswerCard && (
            <Button size="sm" className="rounded-full" onClick={() => setAnswering(true)}>
              {tc('todoCard.open')}
            </Button>
          )}
          {todo.audience === 'internal' && (
            <Button
              size="sm"
              className="rounded-full"
              disabled={sendToClient.isPending}
              onClick={() => sendToClient.mutate(todo.id)}
              data-testid="send-to-client-button"
            >
              {sendToClient.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              {t('sendToClient')}
            </Button>
          )}
        </div>
      </CardContent>
      {canAnswerCard && <ClassifyReceiptDialog todo={todo} open={answering} onOpenChange={setAnswering} />}
    </Card>
  );
}
